// frontend/src/pages/about/AboutPage.tsx
/**
 * Página Sobre - CONEXOR
 * Narrativa da empresa, missão, visão e roadmap
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Favorite as FavoriteIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Build as BuildIcon,
  Rocket as RocketIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { 
  VERDE_MENTA, 
 
  VERDE_MENTA_CLARO,
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  TEXTO_PRIMARIO,
  TEXTO_SECUNDARIO,
  withAlpha
} from '../../themes/themeConstants';
import LandingMenu from '../../components/layout/LandingMenu';

const AboutPage: React.FC = () => {
  const missionValues = [
    {
      icon: <FavoriteIcon fontSize="large" />,
      title: "Missão",
      description: "Empoderar pessoas e empresas com um ecossistema acessível de inteligência artificial, voltado a gerar valor, impulsionar resultados e promover crescimento sustentável."
    },
    {
      icon: <StarIcon fontSize="large" />,
      title: "Visão", 
      description: "Ser reconhecida como uma força transformadora em soluções acessíveis de inteligência artificial, promovendo inovação com simplicidade para organizações brasileiras."
    },
    {
      icon: <GroupsIcon fontSize="large" />,
      title: "Valores",
      description: "Simplicidade com foco no essencial • Transparência e responsabilidade • Aprendizado contínuo e adaptação • Tecnologia centrada em pessoas • Compromisso com a entrega • Crescimento coletivo • Respeito e proteção aos dados"
    }
  ];

  const roadmapItems = [
    {
      phase: "AGORA",
      title: "MVP em Desenvolvimento",
      description: "50+ empresas testando versão beta, moldando cada funcionalidade com feedback real",
      status: "in-progress",
      items: ["Agentes Inteligentes", "Integração WhatsApp", "Dashboard Básico"]
    },
    {
      phase: "90 DIAS",
      title: "Lançamento Oficial", 
      description: "Primeiros clientes pagantes e cases de sucesso documentados",
      status: "planned",
      items: ["Plataforma Estável", "Cases Reais", "Suporte Premium"]
    },
    {
      phase: "6 MESES",
      title: "Ecossistema Completo",
      description: "CRM integrado, analytics comportamental e automações avançadas",
      status: "future",
      items: ["CRM Nativo", "Analytics IA", "Integrações API"]
    },
    {
      phase: "1 ANO",
      title: "Hub de Relacionamento",
      description: "Preditivo de churn, recomendações IA e fidelização automatizada",
      status: "future", 
      items: ["Preditivo Churn", "IA Recomendações", "Fidelização Auto"]
    }
  ];

  const brazilianTech = [
    { icon: <LanguageIcon />, title: "Entende Seu Cliente", desc: "Fala a língua do seu negócio e adapta-se ao perfil dos seus clientes" },
    { icon: <SecurityIcon />, title: "Dados Seguros", desc: "Seus dados ficam protegidos, sem compartilhamento com terceiros" },
    { icon: <BuildIcon />, title: "Integração Simples", desc: "Conecta com WhatsApp, CRM e sistemas que você já usa" },
    { icon: <TrendingUpIcon />, title: "ROI Garantido", desc: "Pague pelo que usar, economize em contratações" }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LandingMenu currentPage="about" />

      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          backgroundColor: FUNDO_PAPEL,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${withAlpha(VERDE_MENTA, 0.15)} 0%, transparent 20%), 
                           radial-gradient(circle at 75% 75%, ${withAlpha(VERDE_MENTA, 0.1)} 0%, transparent 20%)`,
          py: { xs: 12, md: 16 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              label="💼 PARA EMPREENDEDORES"
              sx={{
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                fontWeight: 'bold',
                mb: 4,
                fontSize: '0.9rem'
              }}
            />
            
            <Typography
              component={motion.h1}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                mb: 3,
                color: TEXTO_PRIMARIO,
                lineHeight: 1.1
              }}
            >
              Superpotencialize Seu
              <br />
              <Box component="span" sx={{ 
                background: `linear-gradient(90deg, ${VERDE_MENTA} 0%, #00ACC1 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Relacionamento Com Clientes
              </Box>
            </Typography>
            
            <Typography
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              variant="h5"
              sx={{
                color: TEXTO_SECUNDARIO,
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6,
                mb: 4
              }}
            >
              <strong>Agentes inteligentes que aprendem com você</strong> e trabalham como seus 
              assistentes nas plataformas digitais. Automatizam tarefas, ajudam vendedores no atendimento 
              e se conectam onde seus clientes já estão: WhatsApp, Telegram, Web. 
              A CONEXOR oferece <strong>controle total, autonomia máxima</strong> com IA prática e estratégica.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* O Conceito CONEXOR */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_ESCURO }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
              Nossa Missão
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', maxWidth: '600px', mx: 'auto' }}>
              Democratizar o poder da IA para que todo negócio tenha sua equipe digital inteligente
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{ 
                  height: '100%', 
                  backgroundColor: FUNDO_PAPEL,
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 2 }}>
                    ASSISTIR VENDEDORES
                  </Typography>
                  <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Ajuda seus vendedores com <strong>informações instantâneas</strong>, catálogos, preços.<br/>
                    Trabalha junto com sua equipe nas plataformas digitais.
                    <strong>Vendedor + IA = Atendimento superpotencializado.</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{ 
                  height: '100%', 
                  backgroundColor: FUNDO_PAPEL,
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 2 }}>
                    TRABALHAR AUTÔNOMO
                  </Typography>
                  <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Opera sozinho quando ninguém está online: <strong>prospecta, qualifica, agenda</strong>.<br/>
                    Não perde nenhum lead por falta de atenção ou horário comercial.
                    <strong>Trabalha 24/7 enquanto equipe descansa.</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Nossa História */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_PAPEL }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Chip
                  label="🎯 NOSSO PROPÓSITO"
                  sx={{
                    backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                    color: VERDE_MENTA,
                    fontWeight: 'bold',
                    mb: 3
                  }}
                />
                
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
                  Feito Por e Para
                  <br />
                  <span style={{ color: VERDE_MENTA }}>Empreendedores</span>
                </Typography>
                
                <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                  Conhecemos a realidade do empreendedor: <strong>horas perdidas</strong> respondendo as mesmas perguntas, 
                  classificando leads, gerenciando campanhas... Tempo que deveria estar sendo usado para 
                  <strong style={{ color: VERDE_MENTA }}>fazer o negócio crescer</strong>.
                </Typography>
                
                <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
                  <strong style={{ color: VERDE_MENTA }}>A CONEXOR foi pensada para devolver seu tempo:</strong> 
                  automatiza prospecção, tira dúvidas, qualifica interessados e agenda reuniões. 
                  <strong>Você não fica horas no sistema</strong> - ele trabalha sozinho.
                </Typography>
                
                <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.7 }}>
                  <strong>Resultado?</strong> Mais tempo para estratégia, inovação, relacionamento com grandes clientes.
                  <strong style={{ color: VERDE_MENTA }}>Tempo para focar no crescimento real do seu negócio.</strong>
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                sx={{ textAlign: 'center' }}
              >
                <Paper 
                  elevation={4} 
                  sx={{ 
                    p: 4, 
                    backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                    border: `2px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                    borderRadius: 3
                  }}
                >
                  <PsychologyIcon sx={{ fontSize: 80, color: VERDE_MENTA, mb: 3 }} />
                  <Typography variant="h5" sx={{ color: TEXTO_PRIMARIO, fontWeight: 'bold', mb: 2 }}>
                    CONEXOR libera seu tempo para:
                  </Typography>
                  <Typography sx={{ color: VERDE_MENTA, fontSize: '1.2rem', fontWeight: 'bold' }}>
                    Crescer, Inovar e Liderar
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Missão, Visão, Valores */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_ESCURO }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 2 }}>
              Nossos Fundamentos
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem' }}>
              O que nos move e para onde vamos
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {missionValues.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  whileHover={{ y: -8 }}
                  sx={{
                    height: '100%',
                    backgroundColor: FUNDO_PAPEL,
                    border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                    borderRadius: 3,
                    textAlign: 'center'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ color: VERDE_MENTA, mb: 3 }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 2 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: TEXTO_SECUNDARIO, lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Para Vendedores Também */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_ESCURO }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="💼 PARA SUA EQUIPE DE VENDAS"
              sx={{
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                fontWeight: 'bold',
                mb: 3
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
              Seus Vendedores Superpotencializados
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', maxWidth: '600px', mx: 'auto' }}>
              Não substituímos vendedores. Transformamos cada um em um super-vendedor com IA.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{ 
                  height: '100%', 
                  backgroundColor: FUNDO_PAPEL,
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 2 }}>
                    🤝 Assistente Pessoal
                  </Typography>
                  <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    O agente ajuda seus vendedores com <strong>informações instantâneas</strong>: 
                    preços, estoque, políticas, catálogos. Vendedor focado em relacionamento, 
                    IA cuida dos dados.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card
                component={motion.div}
                whileHover={{ y: -8 }}
                sx={{ 
                  height: '100%', 
                  backgroundColor: FUNDO_PAPEL,
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 2 }}>
                    🔄 Cobertura Total
                  </Typography>
                  <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Quando vendedores estão offline, ocupados ou de férias, 
                    <strong>o agente assume automaticamente</strong>. Cliente sempre atendido, 
                    oportunidade nunca perdida.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tecnologia Brasileira */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_PAPEL }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="🤖 TECNOLOGIA INTELIGENTE"
              sx={{
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                fontWeight: 'bold',
                mb: 3
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
              Seu Empregado Digital
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', maxWidth: '600px', mx: 'auto' }}>
              Criamos IA que entende seu negócio, seus clientes e suas necessidades. 
              Trabalha 24/7, nunca pede aumento e está sempre motivada.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {brazilianTech.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  sx={{ textAlign: 'center', p: 3 }}
                >
                  <Box sx={{ color: VERDE_MENTA, mb: 2 }}>
                    {React.cloneElement(item.icon, { fontSize: 'large' })}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '0.9rem' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Roadmap */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_ESCURO }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
              Onde Estamos
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem' }}>
              Transparência total sobre nosso desenvolvimento
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {roadmapItems.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  sx={{ height: '100%' }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      backgroundColor: FUNDO_PAPEL,
                      border: `2px solid ${item.status === 'in-progress' ? VERDE_MENTA : 
                                           item.status === 'planned' ? VERDE_MENTA_CLARO :
                                           withAlpha(VERDE_MENTA, 0.3)}`,
                      borderRadius: 3,
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    {/* Status Icon */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -15,
                        left: 20,
                        width: 60,
                        height: 30,
                        backgroundColor: item.status === 'in-progress' ? VERDE_MENTA : 
                                        item.status === 'planned' ? VERDE_MENTA_CLARO :
                                        withAlpha(VERDE_MENTA, 0.5),
                        borderRadius: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.status === 'in-progress' ? '#000' : '#fff'
                      }}
                    >
                      {item.status === 'in-progress' ? <ScheduleIcon fontSize="small" /> : 
                       item.status === 'planned' ? <RocketIcon fontSize="small" /> : 
                       <StarIcon fontSize="small" />}
                    </Box>
                    
                    <Chip
                      label={item.phase}
                      size="small"
                      sx={{
                        backgroundColor: item.status === 'in-progress' ? VERDE_MENTA : withAlpha(VERDE_MENTA, 0.3),
                        color: item.status === 'in-progress' ? '#000' : VERDE_MENTA,
                        fontWeight: 'bold',
                        mb: 3,
                        mt: 1
                      }}
                    />
                    
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 2 }}>
                      {item.title}
                    </Typography>
                    
                    <Typography sx={{ color: TEXTO_SECUNDARIO, mb: 3 }}>
                      {item.description}
                    </Typography>
                    
                    <List dense>
                      {item.items.map((subItem, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: VERDE_MENTA }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={subItem} 
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', color: TEXTO_SECUNDARIO } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Final */}
      <Box sx={{ py: { xs: 10, md: 16 }, backgroundColor: FUNDO_PAPEL }}>
        <Container maxWidth="md">
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            sx={{ textAlign: 'center' }}
          >
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO, mb: 3 }}>
              Faça Parte Desta Jornada
            </Typography>
            <Typography sx={{ color: TEXTO_SECUNDARIO, fontSize: '1.1rem', mb: 4 }}>
              Pare de perder vendas por falta de tempo ou recursos. A CONEXOR cuida da parte repetitiva 
              enquanto você foca no que realmente gera resultado: <strong>fechar mais negócios</strong>.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/register?source=about'}
              sx={{
                background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 100%)`,
                color: '#000',
                fontWeight: 'bold',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: '30px',
                boxShadow: `0 8px 30px ${withAlpha(VERDE_MENTA, 0.4)}`,
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 12px 40px ${withAlpha(VERDE_MENTA, 0.6)}`
                }
              }}
            >
              🚀 Participar do Beta
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;